/**
 * cdc-boost-filter.js
 *
 * POC controller for the custom Boost filter/sort drawer.
 * Boost still owns the filter option behavior, AJAX updates, counts, and chips.
 */
(function () {
  'use strict';

  const HIDDEN_CLASS = 'boost-sd__filter-tree-vertical--hidden';
  const OPEN_CLASS = 'is-open';
  const DRAWER_SELECTOR = '.cdc-filter-drawer.boost-sd__filter-tree-vertical-sticky-overlay';
  const SORT_BUTTON_SELECTOR = '.cdc-filter-drawer-sort-button[data-sort-value]';
  const FILTER_CHIPS_SELECTOR = '[data-cdc-filter-chips]';
  const SORT_CHIP_SELECTOR = '[data-cdc-sort-chip]';
  const BEST_SELLING_SORT = 'best-selling';
  const IGNORE_FILTER_PARAMS = new Set([
    'sort',
    'sort_by',
    'page',
    'limit',
    'view',
    'q',
    'options[prefix]',
    'options%5Bprefix%5D',
    'type',
  ]);

  function getDrawerEl() {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    return drawer && drawer.querySelector('.cdc-filter-drawer-panel') ? drawer : null;
  }

  function isDrawerOpen() {
    const drawer = getDrawerEl();
    return !!(drawer && drawer.classList.contains(OPEN_CLASS) && !drawer.classList.contains(HIDDEN_CLASS));
  }

  function setToggleExpanded(expanded) {
    document.querySelectorAll('.boost-sd__filter-tree-toggle-button').forEach((button) => {
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function openDrawer() {
    const drawer = getDrawerEl();
    if (!drawer) {
      unlockPage();
      return false;
    }
    drawer.classList.remove(HIDDEN_CLASS);
    drawer.classList.add(OPEN_CLASS);
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    setToggleExpanded(true);
    return true;
  }

  function closeDrawer() {
    const drawer = getDrawerEl();
    if (drawer) {
      drawer.classList.remove(OPEN_CLASS);
      drawer.classList.add(HIDDEN_CLASS);
    }
    unlockPage();
    setToggleExpanded(false);
  }

  function unlockPage() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.documentElement.classList.remove('boost-sd__no-scroll', 'no-scroll');
    document.body.classList.remove('boost-sd__no-scroll', 'no-scroll');
  }

  // ---------- Filter loading effect ----------
  // While Boost re-fetches after a filter/sort change we surface a progress bar.
  //  - Drawer OPEN  → bar at the top of the drawer panel (visible where the user is,
  //                   on both mobile bottom-sheet and desktop side panel).
  //  - Drawer CLOSED → fixed top-of-viewport bar + dimmed grid (e.g. toolbar chip remove).
  // Hidden on boostPLPProductsLoaded OR as soon as the grid re-renders (MutationObserver).
  let filterLoadingTimeout = null;

  function showFilterLoading() {
    if (isDrawerOpen()) {
      const panel = document.querySelector('.cdc-filter-drawer-panel');
      if (panel) panel.classList.add('cdc-drawer-loading');
    } else {
      if (!document.querySelector('.cdc-filter-loading-bar')) {
        const bar = document.createElement('div');
        bar.className = 'cdc-filter-loading-bar';
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-label', 'Loading products');
        document.body.appendChild(bar);
      }
      document.querySelectorAll('.boost-sd__product-list').forEach((list) => {
        list.classList.add('cdc-is-filtering');
      });
    }
    // Safety net in case nothing re-renders / no event fires.
    clearTimeout(filterLoadingTimeout);
    filterLoadingTimeout = setTimeout(hideFilterLoading, 12000);
  }

  function hideFilterLoading() {
    clearTimeout(filterLoadingTimeout);
    document.querySelectorAll('.cdc-filter-loading-bar').forEach((bar) => bar.remove());
    document.querySelectorAll('.boost-sd__product-list.cdc-is-filtering').forEach((list) => {
      list.classList.remove('cdc-is-filtering');
    });
    document.querySelectorAll('.cdc-filter-drawer-panel.cdc-drawer-loading').forEach((panel) => {
      panel.classList.remove('cdc-drawer-loading');
    });
  }

  function filterLoadingActive() {
    return !!document.querySelector('.cdc-filter-loading-bar, .cdc-filter-drawer-panel.cdc-drawer-loading');
  }

  // True when a mutation batch ADDED a product item (or the product list itself) — i.e.
  // Boost has rendered the new results in. That's our cue to hide the loading effect
  // (we ignore removals so we don't hide early while Boost clears the old list).
  function productGridChanged(records) {
    return records.some((record) =>
      Array.prototype.some.call(record.addedNodes, (node) => {
        if (!node || node.nodeType !== 1) return false;
        return (node.matches && node.matches('.boost-sd__product-item, .boost-sd__product-list')) ||
          (node.querySelector && !!node.querySelector('.boost-sd__product-item'));
      })
    );
  }

  function getUrlSortValue() {
    const params = new URLSearchParams(window.location.search);
    return params.get('sort') || params.get('sort_by') || '';
  }

  function applySort(sortValue) {
    if (!sortValue) return;
    showFilterLoading();
    const url = new URL(window.location.href);
    url.searchParams.set('sort', sortValue);
    url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  function getDefaultSortButton() {
    return document.querySelector(SORT_BUTTON_SELECTOR);
  }

  function getActiveSortButton(sortValue) {
    if (sortValue) {
      return Array.from(document.querySelectorAll(SORT_BUTTON_SELECTOR)).find((button) => {
        return button.getAttribute('data-sort-value') === sortValue;
      }) || null;
    }
    return document.querySelector('.cdc-filter-drawer-sort-item--active ' + SORT_BUTTON_SELECTOR) || getDefaultSortButton();
  }

  function setActiveSort(sortValue) {
    const activeButton = getActiveSortButton(sortValue);
    const activeValue = activeButton ? activeButton.getAttribute('data-sort-value') : '';

    document.querySelectorAll('.cdc-filter-drawer-sort-item').forEach((item) => {
      const button = item.querySelector(SORT_BUTTON_SELECTOR);
      const isActive = button && button.getAttribute('data-sort-value') === activeValue;
      item.classList.toggle('cdc-filter-drawer-sort-item--active', isActive);
      if (button) button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    syncSortChip();
  }

  // No `sort` in URL -> use Boost's configured default (Boost applies it without writing
  // to the URL), so we don't wrongly fall back to best-selling.
  function getActiveSortValue() {
    return getUrlSortValue() || getConfiguredDefaultSort();
  }

  // Boost stores the default sort in additionalElementSettings.default_sort_order:
  //   { search: '<search page>', all: '<collection>' }. Search page uses `search`,
  //   everything else uses `all`. Cache only on a hit; if config/drawer aren't ready yet
  //   fall back to best-selling and re-probe on later syncs.
  let configuredDefaultSortCache = '';
  function getConfiguredDefaultSort() {
    if (configuredDefaultSortCache) return configuredDefaultSortCache;

    const validSorts = new Set(
      Array.from(document.querySelectorAll(SORT_BUTTON_SELECTOR))
        .map((button) => button.getAttribute('data-sort-value'))
        .filter(Boolean)
    );
    if (!validSorts.size) return BEST_SELLING_SORT;

    const config =
      window.boostSDAppConfig ||
      (window.boostWidgetIntegration && window.boostWidgetIntegration.config) ||
      null;
    const order = config && config.additionalElementSettings && config.additionalElementSettings.default_sort_order;
    const isSearch = /\/search\b/.test(window.location.pathname) || new URLSearchParams(window.location.search).has('q');
    const value = order ? ((isSearch ? order.search : order.all) || '').trim() : '';

    // Accept only a real drawer sort token, guarding against unexpected values.
    if (value && validSorts.has(value)) {
      configuredDefaultSortCache = value;
      return value;
    }
    return BEST_SELLING_SORT;
  }

  // Read-only indicator chip: mirror the active sort onto the chip label. The label text
  // is taken from the matching drawer sort option so translations / custom labels stay in
  // sync. The chip is not clickable — sorting is changed from within the drawer.
  function syncSortChip() {
    const chip = document.querySelector(SORT_CHIP_SELECTOR);
    if (!chip) return;
    const activeValue = getActiveSortValue();
    const labelEl = chip.querySelector('[data-cdc-sort-label]');
    const sortButton = getActiveSortButton(activeValue);
    if (labelEl && sortButton) {
      const sourceLabel = sortButton.querySelector('.cdc-filter-drawer-sort-label');
      const text = sourceLabel ? normalizeText(sourceLabel.textContent) : '';
      // Guard the write — textContent is a childList mutation our observer watches;
      // writing every sync would loop (observer→syncState→write) and jank the page.
      if (text && labelEl.textContent !== text) labelEl.textContent = text;
    }
    if (chip.getAttribute('data-cdc-sort-value') !== activeValue) {
      chip.setAttribute('data-cdc-sort-value', activeValue);
    }
    if (chip.hidden) chip.hidden = false;
  }

  function normalizeText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function getFilterValueLabel(button) {
    const label = button.querySelector('.boost-sd__filter-option-item-label');
    if (label) return normalizeText(label.textContent);

    const amount = button.querySelector('.boost-sd__filter-option-item-amount');
    if (amount) {
      const cloned = button.cloneNode(true);
      cloned.querySelectorAll('.boost-sd__filter-option-item-amount').forEach((node) => node.remove());
      return normalizeText(cloned.textContent);
    }

    return normalizeText(button.textContent);
  }

  function getSelectedFilterButtons() {
    const candidates = Array.from(document.querySelectorAll(
      '.cdc-filter-drawer-filters [role="checkbox"], ' +
      '.cdc-filter-drawer-filters .boost-sd__filter-option-item-button--selected, ' +
      '.cdc-filter-drawer-filters .boost-sd__filter-option-box-item--selected .boost-sd__filter-option-item-button'
    ));

    return candidates.filter((button, index, all) => {
      if (!button || all.indexOf(button) !== index) return false;
      return button.getAttribute('aria-checked') === 'true' ||
        button.classList.contains('boost-sd__filter-option-item-button--selected') ||
        !!button.closest('.boost-sd__filter-option-box-item--selected');
    });
  }

  function getSelectedFilters() {
    return getSelectedFilterButtons().map((button, index) => {
      const option = button.closest('.cdc-filter-option');
      const optionId = option ? option.getAttribute('data-cdc-filter-option-id') : '';
      const optionLabel = option ? option.getAttribute('data-cdc-filter-option-label') : '';
      const valueLabel = getFilterValueLabel(button);
      const key = button.id || button.getAttribute('data-action') || `${optionId}:${valueLabel}:${index}`;

      button.setAttribute('data-cdc-filter-chip-source', key);

      return {
        key,
        label: valueLabel || optionLabel,
      };
    }).filter((item) => item.label);
  }

  function renderFilterChips() {
    const container = document.querySelector(FILTER_CHIPS_SELECTOR);
    if (!container) return;

    const selectedFilters = getSelectedFilters();
    const signature = selectedFilters.map((item) => `${item.key}=${item.label}`).join('|');
    if (container.getAttribute('data-cdc-signature') === signature) return;

    container.setAttribute('data-cdc-signature', signature);
    container.replaceChildren(...selectedFilters.map((item) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cdc-toolbar-filter-chip';
      chip.setAttribute('data-cdc-filter-chip-key', item.key);
      chip.setAttribute('aria-label', `Remove ${item.label}`);

      const label = document.createElement('span');
      label.textContent = item.label;

      const remove = document.createElement('span');
      remove.className = 'cdc-toolbar-filter-chip-remove';
      remove.setAttribute('aria-hidden', 'true');
      remove.textContent = '×';

      chip.append(label, remove);
      return chip;
    }));
  }

  function removeFilterChip(key) {
    const source = Array.from(document.querySelectorAll('.cdc-filter-drawer-filters [data-cdc-filter-chip-source]')).find((button) => {
      return button.getAttribute('data-cdc-filter-chip-source') === key;
    });

    if (source) source.click();
  }

  function hasActiveFilters() {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (value && !IGNORE_FILTER_PARAMS.has(key)) return true;
    }

    const refineBy = document.getElementById('bc-refine-by');
    if (!refineBy) return false;
    return refineBy.textContent.trim().length > 0 || refineBy.children.length > 0;
  }

  function syncClearButtonState() {
    const clearButton = document.querySelector('.cdc-filter-drawer-clear');
    if (!clearButton) return;
    const enabled = hasActiveFilters();
    clearButton.classList.toggle('is-disabled', !enabled);
    clearButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  }

  function syncState() {
    // getActiveSortValue() resolves a URL without `sort` to Boost's configured default,
    // so the active radio/chip reflect the real order instead of best-selling.
    setActiveSort(getActiveSortValue());
    renderFilterChips();
    syncClearButtonState();
  }

  // Coalesce syncState to at most once per animation frame. The grid MutationObserver
  // fires on every Boost DOM mutation (many per frame); without this guard each one
  // queued its own rAF → syncState (heavy DOM queries) ran dozens of times per frame.
  let syncQueued = false;
  function queueSyncState() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncState();
    });
  }

  function setupEvents() {
    // Registered on `window` (capture) on purpose: Boost binds a document-level capture
    // handler to `.boost-sd__filter-tree-toggle-button` and stopImmediatePropagation's it,
    // which would swallow our open. Capture runs window → document, so a window listener
    // fires BEFORE Boost's document one, letting us open the drawer and block Boost.
    window.addEventListener('click', (event) => {
      if (!event.target.closest('.cdc-toolbar-filter-button')) return;
      if (!openDrawer()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    document.addEventListener('click', (event) => {
      const filterChip = event.target.closest('[data-cdc-filter-chip-key]');
      if (filterChip) {
        event.preventDefault();
        showFilterLoading();
        removeFilterChip(filterChip.getAttribute('data-cdc-filter-chip-key'));
        return;
      }

      if (event.target.closest('.cdc-toolbar-filter-button')) {
        if (openDrawer()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
      }

      if (event.target.closest('.cdc-filter-drawer .boost-sd__button--close, .cdc-filter-drawer-close')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      const sortButton = event.target.closest(SORT_BUTTON_SELECTOR);
      if (sortButton) {
        event.preventDefault();
        applySort(sortButton.getAttribute('data-sort-value'));
        return;
      }

      const overlay = event.target.closest(DRAWER_SELECTOR);
      const panel = event.target.closest('.cdc-filter-drawer-panel');
      if (overlay && !panel && isDrawerOpen()) {
        closeDrawer();
        return;
      }

      const clearButton = event.target.closest('.cdc-filter-drawer-clear.is-disabled');
      if (clearButton) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.target.closest('.cdc-filter-drawer-apply, .cdc-filter-drawer .boost-sd__button--result')) {
        setTimeout(closeDrawer, 50);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isDrawerOpen()) closeDrawer();
    });

    // Show the loading effect when a filter value / clear-all is clicked (Boost runs the
    // AJAX itself; we just surface the in-flight state until boostPLPProductsLoaded fires).
    document.addEventListener('click', (event) => {
      if (event.target.closest(
        '.cdc-filter-drawer-filters .boost-sd__filter-option-item-button, ' +
        '.cdc-filter-drawer-filters [role="checkbox"], ' +
        '.cdc-filter-drawer-filters [data-action], ' +
        '.cdc-filter-drawer-clear:not(.is-disabled)'
      )) {
        showFilterLoading();
      }
    });

    document.addEventListener('boostPLPProductsLoaded', () => {
      hideFilterLoading();
      syncState();
    });
    document.addEventListener('boostSDAppLoaded', () => {
      hideFilterLoading();
      syncState();
    });
  }

  function start() {
    setupEvents();
    syncState();

    new MutationObserver((records) => {
      queueSyncState();

      // Reliable hide: Boost doesn't always re-fire boostPLPProductsLoaded on in-drawer
      // filter updates, so drop the loading effect as soon as the product grid's contents
      // actually change (a product item / list node added or removed).
      if (filterLoadingActive() && productGridChanged(records)) {
        hideFilterLoading();
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });

    setInterval(() => {
      if (!isDrawerOpen()) {
        unlockPage();
        setToggleExpanded(false);
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
